import { z } from 'zod';

import type { CreateOrder, InsuranceV3VerificationResponse } from '~/api/generated/types.gen';
import {
  AdditionalInformationSchema,
  ContactSchema,
  type OrderContact,
  emptyContact,
} from '~/schemas/create-order';
import { PhoneNumberRequiredSchema } from '~/schemas/PhoneNumberSchema';
import { AddressSchema, StateSchema, emptyAddress } from '~/types/address';
import { CarrierTypeSchema } from '~/types/insurance';
import {
  GenderOptions,
  Language,
  PhoneType,
  Relationship,
  humanizeGender,
  humanizeLanguage,
  humanizePhoneType,
  humanizeRelationship,
  identifyGender,
} from '~/types/order';
import {
  ProductCategoriesSchema,
  ProductImagesSchema,
  ProductPropertiesSchema,
  ProductVariantSchema,
} from '~/types/product';
import type { Patient, PatientContact } from '~/types/user';

export const ReferralClinicalIntakeType = {
  TELEHEALTH_REFERRER_PATIENT_SELF_SCHEDULES: 'TELEHEALTH_REFERRER_PATIENT_SELF_SCHEDULES',
  TELEHEALTH_REFERRER_SCHEDULES: 'TELEHEALTH_REFERRER_SCHEDULES',
  SIGNED_DOCUMENTATION: 'SIGNED_DOCUMENTATION',
  E_PRESCRIBE: 'E_PRESCRIBE',
} as const;

export type ReferralClinicalIntakeType =
  (typeof ReferralClinicalIntakeType)[keyof typeof ReferralClinicalIntakeType];

//
// Product / Item
//
const CartItemSchema = z.object({
  product: z.object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    categories: ProductCategoriesSchema,
    images: z.array(ProductImagesSchema).optional(),
    variants: z.array(ProductVariantSchema),
    properties: z.array(ProductPropertiesSchema),
  }),
  selectedProperties: z.array(
    z.object({
      key: z.string(),
      value: z.string(),
    })
  ),
});
export type CartItem = z.infer<typeof CartItemSchema>;

//
// Contact
//
const phoneTypeValues: string[] = Object.values(PhoneType);
const languageValues: string[] = Object.values(Language);
const relationshipValues: string[] = Object.values(Relationship);

const CartPhoneTypeSchema = z
  .string({
    error: (issue) =>
      issue.input === undefined ? 'Phone type is required' : 'Phone type is invalid',
  })
  .min(1, { error: 'Phone type is required' })
  .refine((value) => value === '' || phoneTypeValues.includes(value), {
    error: 'Phone type is invalid',
  });

const CartPreferredLanguageSchema = z
  .string({
    error: (issue) =>
      issue.input === undefined
        ? 'Preferred language is required'
        : 'Preferred language is invalid',
  })
  .min(1, { error: 'Preferred language is required' })
  .refine((value) => value === '' || languageValues.includes(value), {
    error: 'Preferred language is invalid',
  });

const CartRelationshipSchema = z
  .string({
    error: (issue) =>
      issue.input === undefined ? 'Relationship is required' : 'Relationship is invalid',
  })
  .min(1, { error: 'Relationship is required' })
  .refine((value) => value === '' || relationshipValues.includes(value), {
    error: 'Relationship is invalid',
  });

export const CartContactSchema = z.object({
  firstName: ContactSchema.shape.firstName,
  lastName: ContactSchema.shape.lastName,
  phoneNumber: PhoneNumberRequiredSchema,
  phoneType: CartPhoneTypeSchema,
  email: ContactSchema.shape.email,
  preferredLanguage: CartPreferredLanguageSchema,
  relationship: CartRelationshipSchema,
});

//
// Patient
//
export const CartPatientSchema = z.object({
  id: z.string().optional(),
  firstName: z.string().trim().min(2, { error: 'Cannot be empty' }),
  lastName: z.string().trim().min(2, { error: 'Cannot be empty' }),
  dob: z.string().date('Must be a valid date'),
  gender: z.enum(GenderOptions, { error: 'Must make selection' }),
  address: AddressSchema,
});
export type CartPatient = z.infer<typeof CartPatientSchema>;

export const CartPatientMinimiallySetSchema = z.object({
  firstName: CartPatientSchema.shape.firstName,
  lastName: CartPatientSchema.shape.lastName,
});

const LenientCartPatientSchema = CartPatientSchema.extend({
  id: CartPatientSchema.shape.id.catch(undefined),
  firstName: CartPatientSchema.shape.firstName.catch(''),
  lastName: CartPatientSchema.shape.lastName.catch(''),
  dob: CartPatientSchema.shape.dob.catch(''),
  gender: CartPatientSchema.shape.gender.catch(GenderOptions.Male),
  address: CartPatientSchema.shape.address.catch(emptyAddress),
});

const emptyCartPatient = {
  id: '',
  firstName: '',
  lastName: '',
  dob: '',
  gender: null as any,
  address: emptyAddress,
};

function toCartPatient(patient: Patient): CartPatient {
  return {
    id: patient.patientId,
    firstName: patient.firstName,
    lastName: patient.lastName,
    dob: patient.dob,
    gender: identifyGender(patient.gender),
    address: patient.addresses[0] ?? emptyAddress,
  };
}

//
// Insurance
//
export const CartInsuranceSchema = z.object({
  uuid: z.uuid().optional(),
  name: z.string().trim().min(1, { error: 'Cannot be empty' }),
  state: z.string().trim().min(1, { error: 'Cannot be empty' }),
  carrierType: CarrierTypeSchema.optional(),
  memberId: z.string().trim().min(1, { error: 'Cannot be empty' }),
});
export type CartInsurance = z.infer<typeof CartInsuranceSchema>;

export const CartSelectedInsuranceSchema = CartInsuranceSchema.extend({
  uuid: z.uuid({ error: 'Insurance carrier must be selected' }),
});

const LenientCartInsuranceSchema = CartInsuranceSchema.extend({
  uuid: CartInsuranceSchema.shape.uuid.catch(undefined),
  name: CartInsuranceSchema.shape.name.catch(''),
  state: CartInsuranceSchema.shape.state.catch(''),
  carrierType: CartInsuranceSchema.shape.carrierType.catch(undefined),
  memberId: CartInsuranceSchema.shape.memberId.catch(''),
});

export const emptyCartInsurance: CartInsurance = {
  uuid: '',
  name: '',
  state: '',
  memberId: '',
};

const medicareCoverageSelectionValues = ['no', 'original_medicare', 'medicare_advantage'] as const;
const medicaidCoverageSelectionValues = ['no', 'state_medicaid', 'managed_medicaid'] as const;

const MedicareCoverageSelectionSchema = z.enum(medicareCoverageSelectionValues);
const MedicaidCoverageSelectionSchema = z.enum(medicaidCoverageSelectionValues);

export const CartMedicareCoverageDraftSchema = z.object({
  selection: MedicareCoverageSelectionSchema.optional(),
  medicareId: z.string().optional(),
  advantageCarrierUuid: z.string().optional(),
  advantageCarrierName: z.string().optional(),
  advantageCarrierState: z.string().optional(),
  advantageMemberId: z.string().optional(),
  advantageMedicareId: z.string().optional(),
});

export const CartMedicaidCoverageDraftSchema = z.object({
  selection: MedicaidCoverageSelectionSchema.optional(),
  medicaidId: z.string().optional(),
  managedCarrierUuid: z.string().optional(),
  managedCarrierName: z.string().optional(),
  managedCarrierState: z.string().optional(),
  managedMemberId: z.string().optional(),
});

//
// Contact
//
const LenientContactSchema = ContactSchema.extend({
  firstName: ContactSchema.shape.firstName.catch(''),
  lastName: ContactSchema.shape.lastName.catch(''),
  phoneNumber: PhoneNumberRequiredSchema.catch(''),
  phoneType: ContactSchema.shape.phoneType.catch(PhoneType.MOBILE),
  email: ContactSchema.shape.email.catch(''),
  preferredLanguage: ContactSchema.shape.preferredLanguage.catch(Language.ENGLISH),
  relationship: ContactSchema.shape.relationship.catch(Relationship.SELF),
});

//
// Additional Info
//
const AdditionalInfoSchema = z.object({
  notes: AdditionalInformationSchema,
});
export type AdditionalInfo = z.infer<typeof AdditionalInfoSchema>;

//
// Cart
//
const CartSchema = z.object({
  items: z.array(CartItemSchema),
  patient: CartPatientSchema.optional(),
  insurance: CartInsuranceSchema.optional(),
  secondaryInsurance: CartInsuranceSchema.optional(),
  insurances: z.array(CartInsuranceSchema).optional(),
  medicareCoverage: CartMedicareCoverageDraftSchema.optional(),
  medicaidCoverage: CartMedicaidCoverageDraftSchema.optional(),
  primaryContact: ContactSchema.optional(),
  secondaryContact: ContactSchema.optional(),
  info: AdditionalInfoSchema.optional(),
  state: StateSchema,
});

export type Cart = z.infer<typeof CartSchema>;

export const LenientCartSchema = z.object({
  items: z.array(CartItemSchema).catch([]),
  patient: LenientCartPatientSchema.optional().catch(emptyCartPatient),
  insurance: LenientCartInsuranceSchema.optional().catch(emptyCartInsurance),
  secondaryInsurance: LenientCartInsuranceSchema.optional().catch(undefined),
  insurances: z.array(LenientCartInsuranceSchema).optional().catch(undefined),
  medicareCoverage: CartMedicareCoverageDraftSchema.optional().catch(undefined),
  medicaidCoverage: CartMedicaidCoverageDraftSchema.optional().catch(undefined),
  primaryContact: LenientContactSchema.optional().catch(emptyContact),
  secondaryContact: LenientContactSchema.optional().catch(undefined),
  info: CartSchema.shape.info.catch({}),
  state: CartSchema.shape.state.catch('NY'),
});

const CompletedCartBaseSchema = CartSchema.extend({
  items: z.array(CartItemSchema).min(1),
  patient: CartPatientSchema,
  insurance: CartSelectedInsuranceSchema.optional(),
  secondaryInsurance: CartSelectedInsuranceSchema.optional(),
  insurances: z.array(CartSelectedInsuranceSchema).optional(),
  primaryContact: CartContactSchema,
  secondaryContact: CartContactSchema.optional(),
});

export function createCompletedCartSchema(isInsuranceCoverageFlowEnabled: boolean) {
  return CompletedCartBaseSchema.superRefine((cart, ctx) => {
    if (isInsuranceCoverageFlowEnabled) {
      if (!cart.insurances || cart.insurances.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['insurances'],
          message: 'At least one insurance is required',
        });
      }
      return;
    }

    const insuranceResult = CartSelectedInsuranceSchema.safeParse(cart.insurance);
    if (!insuranceResult.success) {
      for (const issue of insuranceResult.error.issues) {
        ctx.addIssue({
          code: 'custom',
          path: ['insurance', ...issue.path],
          message: issue.message,
        });
      }
    }
  });
}

export const CompletedCartSchema = createCompletedCartSchema(false);

const CompletedCartInsuranceCoverageSchema = createCompletedCartSchema(true);

type LegacyCompletedCart = z.infer<typeof CompletedCartSchema>;
type InsuranceCoverageCompletedCart = z.infer<typeof CompletedCartInsuranceCoverageSchema>;
type CompletedCart = LegacyCompletedCart | InsuranceCoverageCompletedCart;

export const emptyCart: Cart = {
  items: [],
  state: 'NY',
};

export function getEmptyPatient(cart: Cart): CartPatient {
  return {
    id: '',
    firstName: '',
    lastName: '',
    dob: '',
    gender: null as any,
    address: { ...emptyAddress, state: cart.state },
  };
}

function toOrderContact(contact: PatientContact): OrderContact {
  return {
    firstName: contact.firstName,
    lastName: contact.lastName,
    phoneNumber: contact.phoneNumber,
    phoneType: contact.phoneType ?? '',
    email: contact.email || undefined,
    preferredLanguage: contact.preferredLanguage ?? '',
    relationship: contact.patientRelationship ?? '',
  };
}

export function setPatient(cart: Cart, patient: Patient): Cart {
  return {
    ...cart,
    patient: toCartPatient(patient),
    insurance: patient.insurances[0],
    secondaryInsurance: patient.insurances[1],
    primaryContact: patient.contacts[0] ? toOrderContact(patient.contacts[0]) : undefined,
    secondaryContact: patient.contacts[1] ? toOrderContact(patient.contacts[1]) : undefined,
  };
}

export function removeItem(cart: Cart, productId: string): Cart {
  return {
    ...cart,
    items: cart.items.filter((i) => i.product.id !== productId),
  };
}

export function addItem(cart: Cart, item: CartItem): Cart {
  return {
    ...cart,
    items: [...cart.items.filter((i) => i.product.id !== item.product.id), item],
  };
}

export type TelehealthSchedulingParams = {
  telehealthScheduledTime: string;
  telehealthProviderId: string;
  telehealthTimezone: string;
};

export function toInsuranceVerificationFingerprint(insurance: CartInsurance): string {
  if (!insurance.carrierType) return insurance.uuid ?? '';
  if (!insurance.uuid) return `${insurance.carrierType}-${insurance.memberId}`;

  return `${insurance.uuid}-${insurance.carrierType}`;
}

export function toOrderCreatePayload(
  cart: CompletedCart,
  clinicalIntakeType?: ReferralClinicalIntakeType,
  telehealthParams?: TelehealthSchedulingParams,
  isInsuranceCoverageFlowEnabled = false,
  verificationResult?: InsuranceV3VerificationResponse | null
): CreateOrder {
  const verifiedInsurances = isInsuranceCoverageFlowEnabled
    ? toVerifiedOrderInsurances(cart, verificationResult)
    : null;

  return {
    items: cart.items.map((i) => {
      // TODO This is deceptive, ideally having no properties should result in an undefined variant
      //  Whereas here .every returns true for no selected properties
      const selectedVariant = i.product.variants.find((variant) =>
        i.selectedProperties.every((prop) => variant.attributes[prop.key] === prop.value)
      );
      return {
        product_id: i.product.id,
        variant_key: i.selectedProperties.length === 0 ? '' : selectedVariant?.key,
      };
    }),
    patient: {
      first_name: cart.patient.firstName,
      last_name: cart.patient.lastName,
      dob: cart.patient.dob,
      // @ts-ignore
      gender: humanizeGender(cart.patient.gender as GenderOptions),
    },
    shippingAddress: {
      street: cart.patient.address.street,
      complement: cart.patient.address.complement,
      city: cart.patient.address.city,
      state: cart.patient.address.state,
      zip_code: cart.patient.address.zipCode,
    },
    ...(isInsuranceCoverageFlowEnabled
      ? {
          insurances: verifiedInsurances,
        }
      : { insurance: cart.insurance, secondaryInsurance: cart.secondaryInsurance }),
    primary_contact: {
      first_name: cart.primaryContact.firstName,
      last_name: cart.primaryContact.lastName,
      phone_number: cart.primaryContact.phoneNumber,
      email: cart.primaryContact.email?.trim() || null,
      // @ts-ignore
      phone_type: humanizePhoneType(cart.primaryContact.phoneType as PhoneType),
      // @ts-ignore
      preferred_language: humanizeLanguage(cart.primaryContact.preferredLanguage as Language),
      // @ts-ignore
      relationship: humanizeRelationship(cart.primaryContact.relationship as Relationship),
    },
    // @ts-ignore
    secondary_contact: cart.secondaryContact
      ? {
          first_name: cart.secondaryContact.firstName,
          last_name: cart.secondaryContact.lastName,
          phone_number: cart.secondaryContact.phoneNumber,
          email: cart.secondaryContact.email?.trim() || null,
          phone_type: humanizePhoneType(cart.secondaryContact.phoneType as PhoneType),
          preferred_language: humanizeLanguage(cart.secondaryContact.preferredLanguage as Language),
          relationship: humanizeRelationship(cart.secondaryContact.relationship as Relationship),
        }
      : undefined,
    additional_information: createAdditionalInfo(cart),
    insurance_gated: isInsuranceCoverageFlowEnabled,
    clinical_intake_type: clinicalIntakeType,
    ...(telehealthParams
      ? {
          telehealth_scheduled_time: telehealthParams.telehealthScheduledTime,
          telehealth_provider_id: telehealthParams.telehealthProviderId,
          telehealth_timezone: telehealthParams.telehealthTimezone,
        }
      : {}),
  };
}

function toVerifiedOrderInsurances(
  cart: CompletedCart,
  verificationResult?: InsuranceV3VerificationResponse | null
) {
  if (!verificationResult) {
    throw new Error('Insurance verification is required before creating an order.');
  }

  if (
    !cart.insurances ||
    cart.insurances.length === 0 ||
    verificationResult.payload.length !== cart.insurances.length
  ) {
    throw new Error(
      'Insurance verification is incomplete. Please verify insurance before creating an order.'
    );
  }

  const verificationByFingerprint = new Map(
    verificationResult.payload.map((insurance) => [insurance.fingerprint, insurance])
  );

  return cart.insurances.map((cartInsurance) => {
    const fingerprint = toInsuranceVerificationFingerprint(cartInsurance);
    const insurance = verificationByFingerprint.get(fingerprint);
    if (!insurance) {
      throw new Error(
        'Insurance verification is incomplete. Please verify insurance before creating an order.'
      );
    }

    const verificationKey = insurance.verification_key.trim();
    if (!verificationKey) {
      throw new Error('Insurance verification is missing a verification key.');
    }

    return {
      verification_key: verificationKey,
    };
  });
}

function createAdditionalInfo(cart: CompletedCart): string {
  return `${cart.info?.notes ?? ''}`.trim();
}
